import View                  from '@widesk-ui/components/View';
import Alert                 from '@widesk-ui/components/Alert';
import Progress              from '@widesk-ui/components/Progress';
import Input                 from '@widesk-ui/components/form/Input';
import Select                from '@widesk-ui/components/form/Select';
import { isValidGlyphCharacter }  from '@/TokamapEditor/helpers/planGlyphs';
import { labelCoveragePercent }   from '@/TokamapEditor/helpers/planGlyphs';
import { normalizeGlyphCharacter } from '@/TokamapEditor/helpers/planGlyphs';
import type { PlanGlyphReport } from '@models/map/dxfTypes';
import type { PlanGlyphScope }  from '@models/map/dxfTypes';

/**
 * Étape d'étiquetage du dictionnaire de glyphes.
 *
 * **Ce n'est pas de l'OCR.** Une police vectorisée produit toujours la même forme pour le
 * même caractère : l'opérateur nomme des FORMES une fois, et le serveur relit toutes leurs
 * occurrences par comparaison exacte. D'où l'écran : une vignette, un caractère.
 *
 * Deux propriétés guident l'ergonomie :
 *
 * - la liste arrive **triée par fréquence** — les premières formes débloquent le plus de
 *   libellés, l'opérateur peut s'arrêter en cours de route sans tout perdre ;
 * - la relation forme -> caractère est **many-to-one** : le même « 3 » revient sous
 *   plusieurs tracés, et c'est normal d'en saisir plusieurs identiques.
 *
 * L'étape est FACULTATIVE : sans dictionnaire, les POI sortent sans numéro plutôt que
 * devinés. On le dit, plutôt que de bloquer.
 */
type Props = {
	report: PlanGlyphReport;
	/** Saisies en cours, indexées par signature. */
	values: Record<string, string>;
	scope: PlanGlyphScope;
	disabled?: boolean;
	onChange: (signature: string, value: string) => void;
	onScopeChange: (scope: PlanGlyphScope) => void;
};

const SCOPE_OPTIONS = [
	{ value: 'project', label: 'Ce projet — réutilisé par ses prochains imports' },
	{ value: 'global', label: 'Toutes les cartes — même police ailleurs' },
	{ value: 'import', label: 'Cet import seulement' },
];

const PlanGlyphLabelling = ({ report, values, scope, disabled, onChange, onScopeChange }: Props) => {
	const invalid = Object.entries(values).filter(([, value]) => !isValidGlyphCharacter(value));
	const filled = Object.values(values).filter(value => '' !== normalizeGlyphCharacter(value)).length;

	return (
		<View gap={8}>
			<Alert
				type="info"
				showIcon
				message="Les numéros de ce plan sont vectorisés"
				description={
					`${report.totalLabels} emplacement(s) à lire, dont ${report.readableLabels} déjà lisible(s). `
					+ `Nommez les formes ci-dessous : chacune sera relue partout où elle apparaît. `
					+ `Vous pouvez n'en faire qu'une partie — les POI sans numéro seront simplement créés sans libellé.`
				}
			/>

			<View row gap={8} center>
				<View flex>
					<Progress type="line" percent={labelCoveragePercent(report)} size="small" />
				</View>
				<View size={12} color="#999">
					{report.readableLabels}/{report.totalLabels} lisibles
				</View>
			</View>

			<Select
				value={scope}
				options={SCOPE_OPTIONS}
				disabled={disabled}
				onChange={(value?: unknown) => onScopeChange((value as PlanGlyphScope) ?? 'project')}
			/>

			{!!invalid.length && (
				<Alert
					type="warning"
					showIcon
					message="Saisies non enregistrables"
					description="Un seul caractère par forme, parmi les chiffres, les lettres majuscules, le point, le tiret et l’espace. Les autres seraient ignorées sans avertissement par le serveur."
				/>
			)}

			<View
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
					gap: 8,
				}}
			>
				{report.unresolved.map(glyph => {
					const value = values[glyph.signature] ?? '';
					const wrong = !isValidGlyphCharacter(value);

					return (
						<View
							key={glyph.signature}
							gap={4}
							style={{
								border: `1px solid ${wrong ? '#ff4d4f' : '#f0f0f0'}`,
								borderRadius: 4,
								padding: 6,
							}}
						>
							{/* La vignette est un SVG en data-URI produit par le serveur depuis le tracé
							    d'origine : c'est la forme EXACTE que l'opérateur doit nommer. */}
							<img
								src={glyph.preview}
								alt={`Forme ${glyph.signature}`}
								title={glyph.samples.length ? `Vu sur : ${glyph.samples.join(', ')}` : undefined}
								style={{ width: '100%', height: 40, objectFit: 'contain', background: '#fff' }}
							/>
							<Input
								value={value}
								maxLength={1}
								disabled={disabled}
								placeholder="?"
								onChange={(next?: unknown) => onChange(glyph.signature, normalizeGlyphCharacter(String(next ?? '')))}
							/>
							<View size={11} color="#999">{glyph.occurrences}×</View>
						</View>
					);
				})}
			</View>

			<View size={12} color="#999">
				{filled} forme(s) saisie(s) sur {report.unresolved.length} — police {report.fontKey.slice(0, 8)}
			</View>
		</View>
	);
};

export default PlanGlyphLabelling;
